import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listNFTs,
  fetchPublicOwnerCollection,
  listOwnerGroups,
  createOwnerGroup,
  updateOwnerGroup,
  deleteOwnerGroup,
  listOwnerGroupAssignments,
  bulkOwnerGroupAssignments,
  updateOwnerCollectionVisibility,
  updateOwnerGroupVisibility,
} from "../api/nft";
import { AuthContext } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { fetchUserByUUID } from "../api/users";
import { resolveApiMessage } from "../utils/apiMessages";
import { SORT_FIELD_NONE, sortOwnerItems } from "../utils/ownerSorting";
import {
  VISIBILITY_STATE,
  getGroupVisibilitySummaries,
  getOwnerVisibilitySummary,
} from "../utils/ownerVisibility";
import { getOwnerGroupByOptions, normalizeOwnerGroupBy } from "../utils/ownerGrouping";
import { applyUsMintCodeToTitle, buildMintBadgeValue, buildYearBadgeValue } from "../utils/titleFormatting";

const Badge = ({ children, tone = "neutral" }) => (
  <span className={`owner-badge owner-badge--${tone}`}>{children}</span>
);

const LabelBadge = ({ children, tone = "neutral" }) => (
  <span className={`owner-label-badge owner-label-badge--${tone}`}>{children}</span>
);

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const FALLBACK_OWNER_THUMB_UNTRUSTED = `${PUBLIC_URL}/coin-thumb-64.png`;
const FALLBACK_OWNER_THUMB_TRUSTED = `${PUBLIC_URL}/coin-thumb-64-locked.png`;

const SORT_FIELDS = [
  ["date_added", "owner.sort.field.dateAdded", "Date added"],
  ["title", "owner.sort.field.title", "Title"],
  ["grade", "owner.sort.field.grade", "Grade"],
  ["year", "owner.sort.field.year", "Year"],
  ["denomination", "owner.sort.field.denomination", "Denomination"],
  ["country", "owner.sort.field.country", "Issuer"],
  ["mintage", "owner.sort.field.mintage", "Mintage"],
];

const SORT_DIRECTIONS = [
  ["asc", "owner.sort.direction.asc", "Ascending"],
  ["desc", "owner.sort.direction.desc", "Descending"],
];

const SECONDARY_SORT_FIELDS = [
  [SORT_FIELD_NONE, "owner.sort.field.none", "None"],
  ...SORT_FIELDS,
];

const DEFAULT_FILTERS = {
  state: "all",
  origin: "all",
  country: "all",
  title: "",
  grade: "all",
  year: "all",
  denomination: "all",
  mintageMax: "",
};

const GROUP_NAME_MAX_LENGTH = 40;

const toNumericOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeText = (value) => String(value || "")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .trim()
  .toLowerCase();
const normalizeGroupName = (value) => normalizeText(String(value || "").replace(/\s+/g, " "));

const toEpochOrNull = (value) => {
  if (!value) return null;
  const date = new Date(value);
  const epoch = date.getTime();
  return Number.isNaN(epoch) ? null : epoch;
};

const OwnerListItem = ({
  nft,
  onSelect,
  isConsignedHolding,
  selectionMode,
  selected,
  onToggleSelected,
  groupNames,
}) => {
  const { t } = useTranslation();
  const metadata = nft?.metadata || {};
  const coin = nft?.coin || {};
  const grade = String(nft?.grade || metadata?.grade || coin?.grade || "").trim();
  const rawTitle = metadata.title
    || [coin.denomination, coin.country, coin.year].filter(Boolean).join(" ").trim()
    || metadata.description
    || t("nft.fallbackTitle", { id: nft?.id ?? "" });
  const title = applyUsMintCodeToTitle(rawTitle, coin, metadata);
  const certificationAuthority = nft?.certification_authority;
  const certificationNumber = nft?.certification_number;
  const collectionName = nft?.collection_name || metadata.collection_name || metadata.collection;
  const yearBadgeValue = buildYearBadgeValue(coin, metadata);
  const mintBadgeValue = buildMintBadgeValue(coin, metadata);
  const itemOrigin = String(nft?.item_origin || "").toUpperCase();
  const isTrustedOrigin = itemOrigin === "TRUSTED_CERTIFIED";
  const visibilityLabel = nft?.is_public ? t("nft.visibility.badgePublic") : t("nft.visibility.badgePrivate");
  const visibilityTone = nft?.is_public ? "positive" : "neutral";
  const thumbSrc = nft?.thumbnailUrl
    || nft?.thumbnail_url
    || (isTrustedOrigin ? FALLBACK_OWNER_THUMB_TRUSTED : FALLBACK_OWNER_THUMB_UNTRUSTED);

  const handleClick = () => {
    if (selectionMode) {
      onToggleSelected?.(nft?.id);
      return;
    }
    onSelect(nft);
  };

  return (
    <button
      className={`owner-nft-card ${selected ? "owner-nft-card--selected" : ""}`}
      type="button"
      onClick={handleClick}
    >
      {selectionMode && (
        <span className={`owner-nft-card__selector ${selected ? "owner-nft-card__selector--selected" : ""}`}>
          {selected ? "x" : ""}
        </span>
      )}
      <div className="owner-nft-card__thumb">
        <img src={thumbSrc} alt={`${title} thumbnail`} className="owner-nft-card__thumb-img" loading="lazy" />
      </div>
      <div className="owner-nft-card__body">
        <div className="owner-nft-card__title-row">
          <p className="owner-nft-card__title">{title}</p>
        </div>
        <div className="owner-nft-card__status">
          {grade ? <LabelBadge>{t("nft.badge.grade", { value: grade })}</LabelBadge> : null}
          {yearBadgeValue ? <LabelBadge>{t("nft.badge.year", { value: yearBadgeValue })}</LabelBadge> : null}
          {mintBadgeValue ? <LabelBadge>{t("nft.badge.mint", { value: mintBadgeValue })}</LabelBadge> : null}
          {certificationAuthority ? <Badge>{certificationAuthority}</Badge> : null}
          {certificationNumber ? <Badge>{t("nft.certNumber", { number: certificationNumber })}</Badge> : null}
          <Badge tone={isTrustedOrigin ? "positive" : "neutral"}>
            {isTrustedOrigin ? t("nft.origin.trusted") : t("nft.origin.userGenerated")}
          </Badge>
          {collectionName ? <LabelBadge>{t("nft.collection", { name: collectionName })}</LabelBadge> : null}
          {Array.isArray(groupNames) ? groupNames.slice(0, 2).map((name) => (
            <LabelBadge key={`${nft?.id}-${name}`} tone="positive">{name}</LabelBadge>
          )) : null}
          {Array.isArray(groupNames) && groupNames.length > 2 ? <LabelBadge tone="positive">+{groupNames.length - 2}</LabelBadge> : null}
          <Badge tone={visibilityTone}>{visibilityLabel}</Badge>
          {isConsignedHolding && <Badge tone="neutral">{t("nft.consigned")}</Badge>}
        </div>
      </div>
      {!selectionMode && <div className="owner-nft-card__chevron">{">"}</div>}
    </button>
  );
};

export function OwnerDashboard() {
  const [nfts, setNfts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupAssignments, setGroupAssignments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [groupErrorMessage, setGroupErrorMessage] = useState("");
  const [publicOwner, setPublicOwner] = useState(null);
  const [currentOwnerProfile, setCurrentOwnerProfile] = useState(null);
  const [shareMessage, setShareMessage] = useState(null);
  const [visibilityModal, setVisibilityModal] = useState(null);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [visibilityError, setVisibilityError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [primarySortField, setPrimarySortField] = useState("year");
  const [primarySortDirection, setPrimarySortDirection] = useState("asc");
  const [secondarySortField, setSecondarySortField] = useState("denomination");
  const [secondarySortDirection, setSecondarySortDirection] = useState("asc");
  const [groupBy, setGroupBy] = useState("my_groups");
  const [expandedSections, setExpandedSections] = useState({});

  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkGroupId, setBulkGroupId] = useState("");
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupNameTouched, setNewGroupNameTouched] = useState(false);
  const [groupActionLoading, setGroupActionLoading] = useState(false);

  const { userId, company, companyRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const { numistrailId: publicOwnerNumistrailId } = useParams();
  const { t } = useTranslation();
  const isPublicReadOnly = Boolean(publicOwnerNumistrailId);
  const groupByOptions = useMemo(
    () => getOwnerGroupByOptions(isPublicReadOnly),
    [isPublicReadOnly]
  );

  useEffect(() => {
    setGroupBy((current) => normalizeOwnerGroupBy(current, isPublicReadOnly));
  }, [isPublicReadOnly]);

  const normalizedRole = (companyRole || "").toLowerCase();
  const companyType = String(company?.company_type || "").toUpperCase();
  const isBusinessInventoryUser = ["CERTIFICATION_ENTITY", "INTERMEDIARY", "PLATFORM"].includes(companyType);
  const publicOwnerCompanyType = String(publicOwner?.company?.company_type || "").toUpperCase();
  const isPublicBusinessInventoryOwner = ["CERTIFICATION_ENTITY", "INTERMEDIARY", "PLATFORM"].includes(publicOwnerCompanyType);
  const ownerTitleKey = isPublicReadOnly
    ? (isPublicBusinessInventoryOwner ? "owner.publicInventoryTitle" : "owner.publicCollectionTitle")
    : (isBusinessInventoryUser ? "owner.inventoryTitle" : "owner.title");
  const ownerSearchPlaceholderKey = (isPublicReadOnly ? isPublicBusinessInventoryOwner : isBusinessInventoryUser)
    ? "owner.searchPlaceholderInventory"
    : "owner.searchPlaceholder";
  const isConsignmentEnabled = Boolean(
    company
    && normalizedRole === "admin"
    && (companyType === "INTERMEDIARY"
      || (companyType === "PLATFORM" && company.company_id === "00000000-0000-0000-0000-000000000001"))
  );
  const shareEntityNameKey = isBusinessInventoryUser ? "owner.share.inventoryName" : "owner.share.collectionName";
  const shareEntityNameFallback = isBusinessInventoryUser ? "inventory" : "collection";
  const currentOwnerNumistrailId = currentOwnerProfile?.numistrail_id;
  const publicShareUrl = currentOwnerNumistrailId
    ? `${window.location.origin}/owner/${encodeURIComponent(currentOwnerNumistrailId)}`
    : "";

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const groupsById = useMemo(() => {
    const byId = {};
    groups.forEach((group) => {
      byId[String(group.id)] = group;
    });
    return byId;
  }, [groups]);
  const existingGroupNames = useMemo(
    () => new Set(groups.map((group) => normalizeGroupName(group?.name))),
    [groups]
  );
  const normalizedNewGroupName = useMemo(
    () => String(newGroupName || "").replace(/\s+/g, " ").trim(),
    [newGroupName]
  );
  const newGroupNameError = useMemo(() => {
    if (!normalizedNewGroupName) {
      return t("owner.groups.nameRequired", { defaultValue: "Enter a group name." });
    }
    if (normalizedNewGroupName.length > GROUP_NAME_MAX_LENGTH) {
      return t("owner.groups.nameTooLong", {
        defaultValue: "Use {{max}} characters or fewer.",
        max: GROUP_NAME_MAX_LENGTH,
      });
    }
    if (existingGroupNames.has(normalizeGroupName(normalizedNewGroupName))) {
      return t("owner.groups.nameDuplicate", { defaultValue: "You already have a group with this name." });
    }
    return "";
  }, [existingGroupNames, normalizedNewGroupName, t]);
  const canCreateGroup = useMemo(
    () => !groupActionLoading && !newGroupNameError,
    [groupActionLoading, newGroupNameError]
  );
  const ownerSearchHint = useMemo(
    () => t("owner.searchHint", { defaultValue: "e.g., MS62, 1990, Dollar, US" }),
    [t]
  );
  const hasShareablePublicItems = useMemo(
    () => nfts.some((nft) => (
      Boolean(nft?.is_public)
      && !nft?.delegated_user_id
      && String(nft?.owner_id || "") === String(userId)
    )),
    [nfts, userId]
  );
  const canSharePublicOwnerPage = Boolean(currentOwnerNumistrailId && hasShareablePublicItems);
  const ownerVisibilitySummary = useMemo(
    () => getOwnerVisibilitySummary(nfts, userId),
    [nfts, userId]
  );
  const groupVisibilitySummaries = useMemo(
    () => getGroupVisibilitySummaries(nfts, userId, groupAssignments, groups),
    [groupAssignments, groups, nfts, userId]
  );
  const canManageOwnerVisibility = !isLoading && ownerVisibilitySummary.eligibleCount > 0;

  const handleRetry = () => setReloadKey((current) => current + 1);

  const refreshGroups = useCallback(async () => {
    const [{ data: groupsData }, { data: assignmentsData }] = await Promise.all([
      listOwnerGroups(),
      listOwnerGroupAssignments(),
    ]);
    const nextGroups = Array.isArray(groupsData?.groups) ? groupsData.groups : [];
    const nextAssignments = assignmentsData?.assignments && typeof assignmentsData.assignments === "object"
      ? assignmentsData.assignments
      : {};
    setGroups(nextGroups);
    setGroupAssignments(nextAssignments);
    setExpandedSections((prev) => {
      const next = { ...prev };
      nextGroups.forEach((group) => {
        if (next[group.id] === undefined) next[group.id] = !group.collapsed_by_default;
      });
      if (next.__ungrouped === undefined) next.__ungrouped = true;
      return next;
    });
    return nextGroups;
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isPublicReadOnly) {
      setIsLoading(true);
      setLoadError(false);
      setGroupErrorMessage("");
      setSelectionMode(false);
      setSelectedIds([]);
      setBulkMessage("");
      setBulkError("");
      setShareMessage(null);
      setVisibilityModal(null);
      setVisibilityError("");
      setCurrentOwnerProfile(null);

      fetchPublicOwnerCollection(publicOwnerNumistrailId).then(({ data }) => {
        if (cancelled) return;
        const nextGroups = Array.isArray(data?.groups) ? data.groups : [];
        const assignments = data?.assignments && typeof data.assignments === "object"
          ? data.assignments
          : {};
        setPublicOwner(data?.owner || null);
        setNfts(Array.isArray(data?.nfts) ? data.nfts : []);
        setGroups(nextGroups);
        setGroupAssignments(assignments);
        setExpandedSections((prev) => {
          const next = { ...prev };
          nextGroups.forEach((group) => {
            if (next[group.id] === undefined) next[group.id] = !group.collapsed_by_default;
          });
          if (next.__ungrouped === undefined) next.__ungrouped = true;
          return next;
        });
      }).catch(() => {
        if (cancelled) return;
        setPublicOwner(null);
        setNfts([]);
        setGroups([]);
        setGroupAssignments({});
        setLoadError(true);
      }).finally(() => {
        if (!cancelled) setIsLoading(false);
      });

      return () => { cancelled = true; };
    }

    if (!userId) {
      setNfts([]);
      setGroups([]);
      setGroupAssignments({});
      setPublicOwner(null);
      setCurrentOwnerProfile(null);
      setShareMessage(null);
      setVisibilityModal(null);
      setVisibilityError("");
      setIsLoading(false);
      setLoadError(false);
      return;
    }

    setIsLoading(true);
    setLoadError(false);
    setGroupErrorMessage("");
    setShareMessage(null);
    setVisibilityModal(null);
    setVisibilityError("");

    Promise.allSettled([
      listNFTs(userId, { includeConsigned: isConsignmentEnabled }),
      listOwnerGroups(),
      listOwnerGroupAssignments(),
      fetchUserByUUID(userId),
    ]).then((results) => {
      if (cancelled) return;
      const [nftsResult, groupsResult, assignmentsResult, profileResult] = results;
      setPublicOwner(null);

      if (nftsResult.status === "fulfilled") {
        setNfts(Array.isArray(nftsResult.value?.data) ? nftsResult.value.data : []);
      } else {
        setNfts([]);
        setLoadError(true);
      }

      if (groupsResult.status === "fulfilled") {
        const nextGroups = Array.isArray(groupsResult.value?.data?.groups) ? groupsResult.value.data.groups : [];
        setGroups(nextGroups);
        setExpandedSections((prev) => {
          const next = { ...prev };
          nextGroups.forEach((group) => {
            if (next[group.id] === undefined) next[group.id] = !group.collapsed_by_default;
          });
          if (next.__ungrouped === undefined) next.__ungrouped = true;
          return next;
        });
      } else {
        setGroups([]);
        setGroupErrorMessage(t("owner.groups.loadFailed", { defaultValue: "Group data is temporarily unavailable." }));
      }

      if (assignmentsResult.status === "fulfilled") {
        const assignments = assignmentsResult.value?.data?.assignments;
        setGroupAssignments(assignments && typeof assignments === "object" ? assignments : {});
      } else {
        setGroupAssignments({});
        setGroupErrorMessage(t("owner.groups.loadFailed", { defaultValue: "Group data is temporarily unavailable." }));
      }

      if (profileResult.status === "fulfilled") {
        setCurrentOwnerProfile(profileResult.value?.data || null);
      } else {
        setCurrentOwnerProfile(null);
      }
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [userId, isConsignmentEnabled, isPublicReadOnly, publicOwnerNumistrailId, reloadKey, t]);

  const enrichedItems = useMemo(() => nfts.map((nft) => {
    const metadata = nft?.metadata || {};
    const coin = nft?.coin || {};
    const rawTitle = String(
      metadata.title
      || [coin.denomination, coin.country, coin.year].filter(Boolean).join(" ").trim()
      || metadata.description
      || ""
    ).trim();
    const title = applyUsMintCodeToTitle(rawTitle, coin, metadata);
    const grade = String(nft?.grade ?? metadata.grade ?? coin.grade ?? "").trim();
    const year = toNumericOrNull(coin.year ?? metadata.year);
    const denomination = String(coin.denomination || metadata.denomination || "").trim();
    const country = String(coin.country || metadata.country || "").trim();
    const mint = String(coin.mint || metadata.mint || "").trim();
    const groupIds = Array.isArray(groupAssignments?.[String(nft.id)]) ? groupAssignments[String(nft.id)] : [];
    return {
      ...nft,
      __state: String(nft?.state || "").toLowerCase(),
      __origin: String(nft?.item_origin || "").toUpperCase(),
      __title: title,
      __grade: grade,
      __year: year,
      __denomination: denomination,
      __country: country,
      __mint: mint,
      __mintage: toNumericOrNull(metadata.mintage ?? metadata.coin_mintage ?? metadata.mintage_value),
      __dateAddedEpoch: toEpochOrNull(metadata.added_at || metadata.date_added || metadata.dateAdded || nft?.digitally_certified_at),
      __groupIds: groupIds,
      __groupNames: groupIds.map((id) => groupsById[id]?.name).filter(Boolean),
    };
  }), [nfts, groupAssignments, groupsById]);

  const countryOptions = useMemo(
    () => Array.from(new Set(enrichedItems.map((item) => item.__country).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [enrichedItems]
  );

  const denominationOptions = useMemo(
    () => Array.from(new Set(enrichedItems.map((item) => item.__denomination).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [enrichedItems]
  );

  const gradeOptions = useMemo(
    () => Array.from(new Set(enrichedItems.map((item) => item.__grade).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [enrichedItems]
  );

  const yearOptions = useMemo(
    () => Array.from(new Set(enrichedItems.map((item) => item.__year).filter((value) => value !== null)))
      .sort((a, b) => a - b),
    [enrichedItems]
  );

  const filteredItems = useMemo(() => {
    const term = normalizeText(searchTerm);
    const maxMintage = toNumericOrNull(filters.mintageMax);
    const titleFilter = normalizeText(filters.title);
    const yearFilter = filters.year === "all" ? null : toNumericOrNull(filters.year);

    return enrichedItems.filter((item) => {
      if (term) {
        const haystack = normalizeText([
          item.__title,
          item.__grade,
          item.__year,
          item.__denomination,
          item.__country,
          item.__mint,
          item?.metadata?.title,
          item?.metadata?.description,
          item?.certification_number,
          item?.certification_authority,
          item?.collection_name,
        ].filter(Boolean).join(" "));
        if (!haystack.includes(term)) return false;
      }
      if (titleFilter && !normalizeText(item.__title).includes(titleFilter)) return false;
      if (filters.state !== "all" && item.__state !== filters.state) return false;
      if (filters.origin === "trusted" && item.__origin !== "TRUSTED_CERTIFIED") return false;
      if (filters.origin === "user_generated" && item.__origin !== "USER_GENERATED") return false;
      if (filters.grade !== "all" && item.__grade !== filters.grade) return false;
      if (filters.denomination !== "all" && item.__denomination !== filters.denomination) return false;
      if (yearFilter !== null && item.__year !== yearFilter) return false;
      if (filters.country !== "all" && item.__country !== filters.country) return false;
      if (maxMintage !== null && (item.__mintage === null || item.__mintage > maxMintage)) return false;
      return true;
    });
  }, [enrichedItems, filters, searchTerm]);

  const sortedItems = useMemo(() => {
    return sortOwnerItems(filteredItems, [
      { field: primarySortField, direction: primarySortDirection },
      { field: secondarySortField, direction: secondarySortDirection },
    ]);
  }, [filteredItems, primarySortField, primarySortDirection, secondarySortField, secondarySortDirection]);

  const groupedSections = useMemo(() => {
    if (groupBy === "none") {
      return [{ id: "__all", title: t("owner.groupBy.none", { defaultValue: "All items" }), items: sortedItems, collapsible: false }];
    }
    if (groupBy === "country") {
      const buckets = {};
      sortedItems.forEach((item) => {
        const key = item.__country || t("common.notAvailable");
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(item);
      });
      return Object.keys(buckets).sort((a, b) => a.localeCompare(b)).map((key) => ({
        id: `country:${key}`,
        title: key,
        items: buckets[key],
        collapsible: true,
      }));
    }
    if (groupBy === "state") {
      const buckets = {};
      sortedItems.forEach((item) => {
        const key = item.__state || "private";
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(item);
      });
      return Object.keys(buckets).sort((a, b) => a.localeCompare(b)).map((key) => ({
        id: `state:${key}`,
        title: key,
        items: buckets[key],
        collapsible: true,
      }));
    }
    const sections = groups.map((group) => ({
      id: group.id,
      title: group.name,
      items: sortedItems.filter((item) => item.__groupIds.includes(group.id)),
      collapsible: true,
    }));
    sections.push({
      id: "__ungrouped",
      title: t("owner.ungrouped", { defaultValue: "Ungrouped" }),
      items: sortedItems.filter((item) => item.__groupIds.length === 0),
      collapsible: true,
    });
    return sections;
  }, [groupBy, groups, sortedItems, t]);

  const activeFilterCount = useMemo(() => {
    const allKeyFilters = new Set(["state", "origin", "country", "grade", "year", "denomination"]);
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (allKeyFilters.has(key)) {
        if (value !== "all") count += 1;
      } else if (value !== "") {
        count += 1;
      }
    });
    if (searchTerm.trim()) count += 1;
    return count;
  }, [filters, searchTerm]);

  const handleSelect = (nft) => {
    if (!nft?.id) return;
    const state = { nftSummary: nft };
    if (isPublicReadOnly) {
      state.returnTo = `/owner/${encodeURIComponent(publicOwnerNumistrailId)}`;
    }
    navigate(`/nft/${nft.id}`, { state });
  };

  const handleToggleSelected = (nftId) => {
    if (!nftId) return;
    setSelectedIds((current) => (current.includes(nftId)
      ? current.filter((id) => id !== nftId)
      : [...current, nftId]));
  };

  const copyToClipboard = async (value) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handleSharePublicOwnerPage = async () => {
    if (!canSharePublicOwnerPage || !publicShareUrl) {
      setShareMessage({
        type: "info",
        text: t("owner.share.noPublicItems", {
          defaultValue: "Make at least one item public to share your {{name}}.",
          name: t(shareEntityNameKey, { defaultValue: shareEntityNameFallback }),
        }),
      });
      return;
    }

    const entityName = t(shareEntityNameKey, { defaultValue: shareEntityNameFallback });
    const title = t("owner.share.nativeTitle", {
      defaultValue: "NumisTrail public {{name}}",
      name: entityName,
    });

    try {
      let usedNativeShare = false;
      if (navigator.share) {
        await navigator.share({ title, url: publicShareUrl });
        usedNativeShare = true;
      } else {
        await copyToClipboard(publicShareUrl);
      }
      setShareMessage({
        type: "success",
        text: t(usedNativeShare ? "owner.share.shared" : "owner.share.copied", {
          defaultValue: usedNativeShare ? "Public {{name}} link shared." : "Public {{name}} link copied.",
          name: entityName,
        }),
      });
    } catch (error) {
      if (error?.name === "AbortError") return;
      setShareMessage({
        type: "error",
        text: t("owner.share.failed", { defaultValue: "We couldn't copy the public link. Try again." }),
      });
    }
  };

  const getVisibilityActionLabel = (summary, scope = "owner", compact = false) => {
    const state = summary?.state || VISIBILITY_STATE.EMPTY;
    if (scope === "group") {
      if (state === VISIBILITY_STATE.PUBLIC) {
        return t("owner.visibility.unpublishGroupAction", { defaultValue: "Unpublish group" });
      }
      if (state === VISIBILITY_STATE.MIXED) {
        return t("owner.visibility.manageGroupAction", { defaultValue: "Manage visibility" });
      }
      return t("owner.visibility.publishGroupAction", { defaultValue: "Publish group" });
    }
    if (state === VISIBILITY_STATE.PUBLIC) {
      if (compact) {
        return t("owner.visibility.unpublishAction", { defaultValue: "Unpublish" });
      }
      return t(
        isBusinessInventoryUser
          ? "owner.visibility.unpublishInventoryAction"
          : "owner.visibility.unpublishCollectionAction",
        { defaultValue: isBusinessInventoryUser ? "Unpublish inventory" : "Unpublish collection" }
      );
    }
    if (state === VISIBILITY_STATE.MIXED) {
      if (compact) {
        return t("owner.visibility.action", { defaultValue: "Visibility" });
      }
      return t(
        isBusinessInventoryUser
          ? "owner.visibility.manageInventoryLabel"
          : "owner.visibility.manageCollectionLabel",
        { defaultValue: isBusinessInventoryUser ? "Manage inventory visibility" : "Manage collection visibility" }
      );
    }
    if (compact) {
      return t("owner.visibility.publishAction", { defaultValue: "Publish" });
    }
    return t(
      isBusinessInventoryUser
        ? "owner.publish.inventoryAction"
        : "owner.publish.collectionAction",
      { defaultValue: isBusinessInventoryUser ? "Publish inventory" : "Publish collection" }
    );
  };

  const openVisibilityModal = (scope, group = null) => {
    setVisibilityError("");
    setVisibilityModal({
      scope,
      groupId: group?.id ? String(group.id) : null,
      groupName: group?.name || "",
    });
  };

  const handleVisibilityChange = async (isPublic) => {
    if (!visibilityModal || visibilityLoading) return;
    const isGroupScope = visibilityModal.scope === "group";
    const summary = isGroupScope
      ? groupVisibilitySummaries[visibilityModal.groupId]
      : ownerVisibilitySummary;
    const affectedCount = isPublic ? summary?.privateCount : summary?.publicCount;
    if (!affectedCount) return;

    setVisibilityLoading(true);
    setVisibilityError("");
    try {
      const { data } = isGroupScope
        ? await updateOwnerGroupVisibility(visibilityModal.groupId, isPublic)
        : await updateOwnerCollectionVisibility(isPublic);
      const updatedCount = Number(data?.updated_count || 0);
      const excludedCount = Number(data?.excluded_consigned_count || 0);
      setNfts((current) => current.map((nft) => (
        String(nft?.owner_id || "") === String(userId)
        && !nft?.delegated_user_id
        && (!isGroupScope || (groupAssignments[String(nft?.id)] || [])
          .map((groupId) => String(groupId))
          .includes(visibilityModal.groupId))
          ? { ...nft, is_public: Boolean(isPublic) }
          : nft
      )));
      setVisibilityModal(null);
      setShareMessage({
        type: "success",
        text: [
          t(isPublic ? "owner.publish.success" : "owner.visibility.unpublishSuccess", {
            defaultValue: isPublic
              ? "Published {{count}} items."
              : "Made {{count}} items private.",
            count: updatedCount,
          }),
          excludedCount > 0 ? t("owner.publish.excluded", {
            defaultValue: "{{count}} consigned items were excluded.",
            count: excludedCount,
          }) : "",
        ].filter(Boolean).join(" "),
      });
    } catch (error) {
      setVisibilityError(resolveApiMessage(error, t, "errors.generic", t("errors.generic")));
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    setNewGroupNameTouched(true);
    if (newGroupNameError) return;
    setGroupActionLoading(true);
    setGroupErrorMessage("");
    try {
      const { data } = await createOwnerGroup({
        name: normalizedNewGroupName,
        collapsed_by_default: true,
      });
      await refreshGroups();
      setBulkGroupId((current) => current || data?.id || "");
      setNewGroupName("");
      setNewGroupNameTouched(false);
    } catch (error) {
      setGroupErrorMessage(resolveApiMessage(error, t, "errors.generic", t("errors.generic")));
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleRenameGroup = async (group) => {
    const nextName = window.prompt(t("owner.groups.renamePrompt", { defaultValue: "New group name" }), group?.name || "");
    if (!nextName || nextName.trim() === String(group?.name || "").trim()) return;
    setGroupActionLoading(true);
    setGroupErrorMessage("");
    try {
      await updateOwnerGroup(group.id, { name: nextName.trim() });
      await refreshGroups();
    } catch (error) {
      setGroupErrorMessage(resolveApiMessage(error, t, "errors.generic", t("errors.generic")));
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    const confirmed = window.confirm(t("owner.groups.deleteConfirm", {
      defaultValue: "Delete this group? Items will remain in your collection.",
    }));
    if (!confirmed) return;
    setGroupActionLoading(true);
    setGroupErrorMessage("");
    try {
      await deleteOwnerGroup(group.id);
      await refreshGroups();
      setBulkGroupId((current) => (current === group.id ? "" : current));
    } catch (error) {
      setGroupErrorMessage(resolveApiMessage(error, t, "errors.generic", t("errors.generic")));
    } finally {
      setGroupActionLoading(false);
    }
  };

  const applyBulkAssignments = async (action) => {
    if (selectedIds.length === 0) return;
    if ((action === "add" || action === "remove" || action === "replace") && !bulkGroupId) {
      setBulkError(t("owner.bulk.groupRequired", { defaultValue: "Select a group first." }));
      return;
    }
    setBulkLoading(true);
    setBulkError("");
    setBulkMessage("");
    try {
      const payload = { action, nft_ids: selectedIds, group_ids: bulkGroupId ? [bulkGroupId] : [] };
      const { data } = await bulkOwnerGroupAssignments(payload);
      const nextAssignments = data?.assignments && typeof data.assignments === "object" ? data.assignments : {};
      const processed = Array.isArray(data?.processed_nft_ids) ? data.processed_nft_ids : [];
      setGroupAssignments((current) => {
        const next = { ...current };
        processed.forEach((nftId) => {
          const key = String(nftId);
          if (nextAssignments[key]) next[key] = nextAssignments[key];
          else delete next[key];
        });
        return next;
      });
      await refreshGroups();
      setSelectedIds([]);
      setBulkMessage(t("owner.bulk.updated", { defaultValue: "Updated {{count}} items.", count: processed.length }));
    } catch (error) {
      setBulkError(resolveApiMessage(error, t, "errors.generic", t("errors.generic")));
    } finally {
      setBulkLoading(false);
    }
  };

  const expandAllSections = () => {
    setExpandedSections((current) => {
      const next = { ...current };
      groupedSections.forEach((section) => {
        if (section.collapsible) next[section.id] = true;
      });
      return next;
    });
  };

  const collapseAllSections = () => {
    setExpandedSections((current) => {
      const next = { ...current };
      groupedSections.forEach((section) => {
        if (section.collapsible) next[section.id] = false;
      });
      return next;
    });
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((current) => ({ ...current, [sectionId]: !(current?.[sectionId] ?? true) }));
  };

  const emptyState = (
    <div className="owner-empty">
      <div className="owner-empty__art" aria-hidden="true">[]</div>
      <p className="owner-empty__title">{t("owner.emptyTitle")}</p>
      <p className="owner-empty__text">{t("owner.emptyText")}</p>
    </div>
  );

  const loadingState = (
    <div className="owner-empty" role="status" aria-live="polite">
      <div className="owner-empty__art" aria-hidden="true">...</div>
      <p className="owner-empty__title">{t("owner.loadingTitle")}</p>
      <p className="owner-empty__text">{t("owner.loadingText")}</p>
    </div>
  );

  const errorState = (
    <div className="owner-empty" role="alert">
      <div className="owner-empty__art" aria-hidden="true">!</div>
      <p className="owner-empty__title">{t("owner.loadErrorTitle")}</p>
      <p className="owner-empty__text">{t("owner.loadErrorText")}</p>
      <button type="button" className="owner-empty__button" onClick={handleRetry}>{t("common.retry")}</button>
    </div>
  );

  const activeVisibilitySummary = visibilityModal?.scope === "group"
    ? groupVisibilitySummaries[visibilityModal?.groupId]
    : ownerVisibilitySummary;
  const activeVisibilityState = activeVisibilitySummary?.state || VISIBILITY_STATE.EMPTY;
  const activeVisibilityName = visibilityModal?.scope === "group"
    ? visibilityModal.groupName
    : t(shareEntityNameKey, { defaultValue: shareEntityNameFallback });
  const visibilityModalTitle = visibilityModal?.scope === "group"
    ? t(
        activeVisibilityState === VISIBILITY_STATE.MIXED
          ? "owner.visibility.manageGroupTitle"
          : activeVisibilityState === VISIBILITY_STATE.PUBLIC
            ? "owner.visibility.unpublishGroupTitle"
            : "owner.visibility.publishGroupTitle",
        {
          defaultValue: activeVisibilityState === VISIBILITY_STATE.MIXED
            ? "Manage visibility for {{name}}"
            : activeVisibilityState === VISIBILITY_STATE.PUBLIC
              ? "Unpublish {{name}}?"
              : "Publish {{name}}?",
          name: activeVisibilityName,
        }
      )
    : t(
        activeVisibilityState === VISIBILITY_STATE.MIXED
          ? "owner.visibility.manageTitle"
          : activeVisibilityState === VISIBILITY_STATE.PUBLIC
            ? "owner.visibility.unpublishTitle"
            : "owner.publish.title",
        {
          defaultValue: activeVisibilityState === VISIBILITY_STATE.MIXED
            ? "Manage {{name}} visibility"
            : activeVisibilityState === VISIBILITY_STATE.PUBLIC
              ? "Unpublish {{name}}?"
              : "Publish {{name}}?",
          name: activeVisibilityName,
        }
      );

  return (
    <div className="owner-screen">
      <div className="owner-shell owner-shell--wide">
        <header className="owner-header owner-header--stack">
          <div className="owner-header__top">
            <h1 className="owner-header__title">
              {t(ownerTitleKey, {
                defaultValue: isPublicReadOnly
                  ? (isPublicBusinessInventoryOwner ? "{{owner}} public inventory" : "{{owner}} public collection")
                  : (isBusinessInventoryUser ? "Inventory" : "My collection"),
                owner: publicOwner?.numistrail_id || publicOwnerNumistrailId,
              })}
            </h1>
            {!isPublicReadOnly && (
              <div className="owner-header__share">
                <button
                  type="button"
                  className="owner-header__share-button owner-header__share-button--visibility"
                  onClick={() => openVisibilityModal("owner")}
                  disabled={!canManageOwnerVisibility}
                  aria-label={getVisibilityActionLabel(ownerVisibilitySummary)}
                  title={!canManageOwnerVisibility
                    ? t("owner.publish.noneEligible", {
                        defaultValue: "There are no eligible items to manage.",
                      })
                    : getVisibilityActionLabel(ownerVisibilitySummary)}
                >
                  {getVisibilityActionLabel(ownerVisibilitySummary, "owner", true)}
                </button>
                <button
                  type="button"
                  className="owner-header__share-button"
                  onClick={handleSharePublicOwnerPage}
                  disabled={!canSharePublicOwnerPage}
                  title={!canSharePublicOwnerPage
                    ? t("owner.share.noPublicItems", {
                        defaultValue: "Make at least one item public to share your {{name}}.",
                        name: t(shareEntityNameKey, { defaultValue: shareEntityNameFallback }),
                      })
                    : publicShareUrl}
                >
                  {t("owner.share.action", { defaultValue: "Share" })}
                </button>
              </div>
            )}
          </div>
          {!isPublicReadOnly && shareMessage ? (
            <p className={`owner-header__share-message owner-header__share-message--${shareMessage.type}`} role="status">
              {shareMessage.text}
            </p>
          ) : null}
          {!isPublicReadOnly && !isLoading && !canSharePublicOwnerPage && currentOwnerNumistrailId ? (
            <p className="owner-header__share-hint">
              {t("owner.share.noPublicItems", {
                defaultValue: "Make at least one item public to share your {{name}}.",
                name: t(shareEntityNameKey, { defaultValue: shareEntityNameFallback }),
              })}
            </p>
          ) : null}
          <div className="owner-header__actions">
            <span className="owner-header__tooltip" data-tooltip={t("owner.actionsHelp.filter", { defaultValue: "Open filters to narrow the list." })}>
              <button
                type="button"
                className={`owner-header__chip ${filterOpen ? "owner-header__chip--active" : ""}`}
                onClick={() => setFilterOpen((prev) => !prev)}
              >
                {t("common.filter")} {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
              </button>
            </span>
            <span className="owner-header__tooltip" data-tooltip={t("owner.actionsHelp.sort", { defaultValue: "Choose how items are ordered." })}>
              <button
                type="button"
                className={`owner-header__chip ${sortOpen ? "owner-header__chip--active" : ""}`}
                onClick={() => setSortOpen((prev) => !prev)}
              >
                {t("common.sort")}
              </button>
            </span>
            <span className="owner-header__tooltip" data-tooltip={t("owner.actionsHelp.group", { defaultValue: "Change how items are grouped." })}>
              <button
                type="button"
                className={`owner-header__chip ${groupOpen ? "owner-header__chip--active" : ""}`}
                onClick={() => setGroupOpen((prev) => !prev)}
              >
                {t("owner.groupBy.label", { defaultValue: "Group" })}
              </button>
            </span>
            {!isPublicReadOnly && (
              <span className="owner-header__tooltip" data-tooltip={t("owner.actionsHelp.select", { defaultValue: "Enable multi-select for bulk actions." })}>
                <button
                  type="button"
                  className={`owner-header__chip ${selectionMode ? "owner-header__chip--active" : ""}`}
                  onClick={() => {
                    setSelectionMode((prev) => !prev);
                    setSelectedIds([]);
                    setBulkMessage("");
                    setBulkError("");
                  }}
                >
                  {t("owner.select", { defaultValue: "Select" })}
                </button>
              </span>
            )}
          </div>
          <div className="owner-header__search-row">
            <input
              className="owner-header__search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t(ownerSearchPlaceholderKey, {
                defaultValue: (isPublicReadOnly ? isPublicBusinessInventoryOwner : isBusinessInventoryUser)
                  ? "Search inventory"
                  : "Search collection",
              })}
            />
            <p className="owner-header__search-hint">
              {ownerSearchHint}
            </p>
          </div>
        </header>

        {filterOpen && (
          <section className="owner-panel">
            <div className="owner-panel__grid">
              <label className="owner-panel__field"><span>{t("owner.filters.title", { defaultValue: "Title" })}</span>
                <input value={filters.title} onChange={(e) => setFilters((c) => ({ ...c, title: e.target.value }))} />
              </label>
              <label className="owner-panel__field"><span>{t("owner.filters.grade", { defaultValue: "Grade" })}</span>
                <select value={filters.grade} onChange={(e) => setFilters((c) => ({ ...c, grade: e.target.value }))}>
                  <option value="all">{t("owner.filters.all", { defaultValue: "All" })}</option>
                  {gradeOptions.map((grade) => <option key={grade} value={grade}>{grade}</option>)}
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.filters.year", { defaultValue: "Year" })}</span>
                <select value={filters.year} onChange={(e) => setFilters((c) => ({ ...c, year: e.target.value }))}>
                  <option value="all">{t("owner.filters.all", { defaultValue: "All" })}</option>
                  {yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.filters.denomination", { defaultValue: "Denomination" })}</span>
                <select value={filters.denomination} onChange={(e) => setFilters((c) => ({ ...c, denomination: e.target.value }))}>
                  <option value="all">{t("owner.filters.all", { defaultValue: "All" })}</option>
                  {denominationOptions.map((denomination) => <option key={denomination} value={denomination}>{denomination}</option>)}
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.filters.state", { defaultValue: "State" })}</span>
                <select value={filters.state} onChange={(e) => setFilters((c) => ({ ...c, state: e.target.value }))}>
                  <option value="all">{t("owner.filters.all", { defaultValue: "All" })}</option>
                  <option value="private">{t("owner.filters.private", { defaultValue: "Private" })}</option>
                  <option value="exposed">{t("owner.filters.exposed", { defaultValue: "Exposed" })}</option>
                  <option value="consigned">{t("owner.filters.consigned", { defaultValue: "Consigned" })}</option>
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.filters.origin", { defaultValue: "Origin" })}</span>
                <select value={filters.origin} onChange={(e) => setFilters((c) => ({ ...c, origin: e.target.value }))}>
                  <option value="all">{t("owner.filters.all", { defaultValue: "All" })}</option>
                  <option value="trusted">{t("nft.origin.trusted")}</option>
                  <option value="user_generated">{t("nft.origin.userGenerated")}</option>
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.filters.country", { defaultValue: "Country" })}</span>
                <select value={filters.country} onChange={(e) => setFilters((c) => ({ ...c, country: e.target.value }))}>
                  <option value="all">{t("owner.filters.all", { defaultValue: "All" })}</option>
                  {countryOptions.map((country) => <option key={country} value={country}>{country}</option>)}
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.filters.mintageMax", { defaultValue: "Mintage max" })}</span>
                <input value={filters.mintageMax} onChange={(e) => setFilters((c) => ({ ...c, mintageMax: e.target.value }))} inputMode="numeric" />
              </label>
            </div>
            <div className="owner-panel__actions">
              <button type="button" className="owner-empty__button" onClick={() => setFilters(DEFAULT_FILTERS)}>
                {t("owner.filters.clear", { defaultValue: "Clear filters" })}
              </button>
            </div>
          </section>
        )}

        {sortOpen && (
          <section className="owner-panel">
            <div className="owner-panel__stack">
              <label className="owner-panel__field"><span>{t("owner.sort.primary.field.label", { defaultValue: "Primary sort" })}</span>
                <select value={primarySortField} onChange={(e) => setPrimarySortField(e.target.value)}>
                  {SORT_FIELDS.map(([value, key, fallback]) => <option key={value} value={value}>{t(key, { defaultValue: fallback })}</option>)}
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.sort.primary.direction.label", { defaultValue: "Primary direction" })}</span>
                <select value={primarySortDirection} onChange={(e) => setPrimarySortDirection(e.target.value)}>
                  {SORT_DIRECTIONS.map(([value, key, fallback]) => <option key={value} value={value}>{t(key, { defaultValue: fallback })}</option>)}
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.sort.secondary.field.label", { defaultValue: "Then by" })}</span>
                <select value={secondarySortField} onChange={(e) => setSecondarySortField(e.target.value)}>
                  {SECONDARY_SORT_FIELDS.map(([value, key, fallback]) => <option key={value} value={value}>{t(key, { defaultValue: fallback })}</option>)}
                </select>
              </label>
              <label className="owner-panel__field"><span>{t("owner.sort.secondary.direction.label", { defaultValue: "Then direction" })}</span>
                <select
                  value={secondarySortDirection}
                  onChange={(e) => setSecondarySortDirection(e.target.value)}
                  disabled={secondarySortField === SORT_FIELD_NONE}
                >
                  {SORT_DIRECTIONS.map(([value, key, fallback]) => <option key={value} value={value}>{t(key, { defaultValue: fallback })}</option>)}
                </select>
              </label>
            </div>
          </section>
        )}

        {groupOpen && (
          <section className="owner-panel">
            <label className="owner-panel__field"><span>{t("owner.groupBy.label", { defaultValue: "Group by" })}</span>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                {groupByOptions.map(([value, key, fallback]) => <option key={value} value={value}>{t(key, { defaultValue: fallback })}</option>)}
              </select>
            </label>
            <div className="owner-panel__actions">
              <button type="button" className="owner-empty__button" onClick={expandAllSections}>{t("owner.expandAll", { defaultValue: "Expand all" })}</button>
              <button type="button" className="owner-empty__button" onClick={collapseAllSections}>{t("owner.collapseAll", { defaultValue: "Collapse all" })}</button>
            </div>
            {!isPublicReadOnly && (
            <div className="owner-groups-manager">
              <p className="owner-groups-manager__title">{t("owner.groups.manage", { defaultValue: "Manage groups" })}</p>
              <div className="owner-groups-manager__create">
                <label className="owner-groups-manager__field">
                  <span className="owner-groups-manager__field-label">
                    {t("owner.groups.nameLabel", { defaultValue: "Group name" })}
                  </span>
                  <input
                    className={`owner-groups-manager__input ${newGroupNameTouched && newGroupNameError ? "owner-groups-manager__input--invalid" : ""}`}
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onBlur={() => setNewGroupNameTouched(true)}
                    placeholder={t("owner.groups.createPlaceholder", { defaultValue: "New group name" })}
                    maxLength={GROUP_NAME_MAX_LENGTH}
                    aria-invalid={newGroupNameTouched && Boolean(newGroupNameError)}
                    aria-describedby={newGroupNameTouched && newGroupNameError ? "owner-group-name-error" : undefined}
                  />
                  <div className="owner-groups-manager__field-foot">
                    <p className="owner-groups-manager__hint">
                      {t("owner.groups.nameHint", {
                        defaultValue: "Required, unique, up to {{max}} characters.",
                        max: GROUP_NAME_MAX_LENGTH,
                      })}
                    </p>
                    <p className="owner-groups-manager__counter">
                      {normalizedNewGroupName.length}/{GROUP_NAME_MAX_LENGTH}
                    </p>
                  </div>
                  {newGroupNameTouched && newGroupNameError ? (
                    <p className="owner-groups-manager__error" id="owner-group-name-error">{newGroupNameError}</p>
                  ) : null}
                </label>

                <button
                  type="button"
                  className="owner-empty__button owner-groups-manager__create-button"
                  onClick={handleCreateGroup}
                  disabled={!canCreateGroup}
                >
                  {groupActionLoading ? t("common.saving") : t("owner.groups.create", { defaultValue: "Create group" })}
                </button>
              </div>
              {groupErrorMessage ? <p className="owner-panel__error">{groupErrorMessage}</p> : null}
              <div className="owner-groups-manager__list">
                {groups.map((group) => {
                  const summary = groupVisibilitySummaries[String(group.id)];
                  return (
                    <div key={group.id} className="owner-groups-manager__item">
                      <div className="owner-groups-manager__item-main">
                        <strong>{group.name}</strong>
                        <span>{t("owner.groups.itemsCount", { defaultValue: "{{count}} items", count: group.item_count || 0 })}</span>
                      </div>
                      <div className="owner-groups-manager__item-actions">
                        <button
                          type="button"
                          className="owner-groups-manager__visibility-action"
                          onClick={() => openVisibilityModal("group", group)}
                          disabled={visibilityLoading || !summary || summary.state === VISIBILITY_STATE.EMPTY}
                        >
                          {getVisibilityActionLabel(summary, "group")}
                        </button>
                        <button type="button" onClick={() => handleRenameGroup(group)} disabled={groupActionLoading || visibilityLoading}>{t("owner.groups.rename", { defaultValue: "Rename" })}</button>
                        <button type="button" onClick={() => handleDeleteGroup(group)} disabled={groupActionLoading || visibilityLoading}>{t("owner.groups.delete", { defaultValue: "Delete" })}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </section>
        )}

        {!isPublicReadOnly && selectionMode && (
          <section className="owner-panel owner-panel--selection">
            <p className="owner-panel__selection-text">{t("owner.bulk.selected", { defaultValue: "{{count}} selected", count: selectedIds.length })}</p>
            <div className="owner-panel__selection-controls">
              <select value={bulkGroupId} onChange={(e) => setBulkGroupId(e.target.value)}>
                <option value="">{t("owner.bulk.selectGroup", { defaultValue: "Select group" })}</option>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
              </select>
              <button type="button" onClick={() => applyBulkAssignments("add")} disabled={bulkLoading || selectedIds.length === 0}>{t("owner.bulk.add", { defaultValue: "Add to group" })}</button>
              <button type="button" onClick={() => applyBulkAssignments("remove")} disabled={bulkLoading || selectedIds.length === 0}>{t("owner.bulk.remove", { defaultValue: "Remove from group" })}</button>
              <button type="button" onClick={() => applyBulkAssignments("replace")} disabled={bulkLoading || selectedIds.length === 0}>{t("owner.bulk.move", { defaultValue: "Move to group" })}</button>
              <button type="button" onClick={() => setSelectedIds([])}>{t("owner.bulk.clear", { defaultValue: "Clear" })}</button>
            </div>
            {bulkError ? <p className="owner-panel__error">{bulkError}</p> : null}
            {bulkMessage ? <p className="owner-panel__success">{bulkMessage}</p> : null}
          </section>
        )}

        {groupErrorMessage && !groupOpen ? <section className="owner-inline-notice" role="status">{groupErrorMessage}</section> : null}

        <section className="owner-list" aria-live="polite">
          {isLoading
            ? loadingState
            : loadError
              ? errorState
              : sortedItems.length === 0
                ? emptyState
                : groupedSections.map((section) => {
                    const isExpanded = section.collapsible ? (expandedSections?.[section.id] ?? true) : true;
                    return (
                      <div className="owner-group" key={section.id}>
                        {section.collapsible && (
                          <button type="button" className="owner-group__header" onClick={() => toggleSection(section.id)}>
                            <span className="owner-group__title">{section.title}</span>
                            <span className="owner-group__meta">{section.items.length} {isExpanded ? "v" : ">"}</span>
                          </button>
                        )}
                        {isExpanded && (
                          <div className="owner-group__items">
                            {section.items.length === 0
                              ? <p className="owner-group__empty">{t("owner.groupEmpty", { defaultValue: "No items in this group." })}</p>
                              : section.items.map((nft) => (
                                <OwnerListItem
                                  key={`${section.id}-${nft.id}`}
                                  nft={nft}
                                  onSelect={handleSelect}
                                  isConsignedHolding={!isPublicReadOnly && Boolean(nft.delegated_user_id && userId && String(nft.delegated_user_id) === String(userId))}
                                  selectionMode={!isPublicReadOnly && selectionMode}
                                  selected={selectedSet.has(nft.id)}
                                  onToggleSelected={handleToggleSelected}
                                  groupNames={nft.__groupNames}
                                />
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
        </section>
      </div>
      {visibilityModal ? (
        <div className="owner-publish-modal" role="dialog" aria-modal="true" aria-labelledby="owner-publish-title">
          <div
            className="owner-publish-modal__backdrop"
            onClick={() => {
              if (!visibilityLoading) setVisibilityModal(null);
            }}
          />
          <div className="owner-publish-modal__card">
            <header className="owner-publish-modal__header">
              <h2 id="owner-publish-title">{visibilityModalTitle}</h2>
              <button
                type="button"
                className="owner-publish-modal__close"
                aria-label={t("common.close", { defaultValue: "Close" })}
                onClick={() => setVisibilityModal(null)}
                disabled={visibilityLoading}
              >
                x
              </button>
            </header>
            <div className="owner-publish-modal__body">
              <p>{t("owner.visibility.privateCount", {
                defaultValue: "{{count}} eligible private items.",
                count: activeVisibilitySummary?.privateCount || 0,
              })}</p>
              <p>{t("owner.visibility.publicCount", {
                defaultValue: "{{count}} eligible public items.",
                count: activeVisibilitySummary?.publicCount || 0,
              })}</p>
              <p>{t("owner.visibility.consignedCount", {
                defaultValue: "{{count}} consigned items are excluded from visibility changes.",
                count: activeVisibilitySummary?.excludedConsignedCount || 0,
              })}</p>
              {visibilityModal.scope === "group" && activeVisibilitySummary?.sharedItemCount > 0 ? (
                <p className="owner-publish-modal__shared">
                  {t("owner.visibility.sharedCount", {
                    defaultValue: "{{count}} items also belong to other groups. Their visibility will change there too.",
                    count: activeVisibilitySummary.sharedItemCount,
                  })}
                </p>
              ) : null}
              <p className="owner-publish-modal__warning">
                {activeVisibilityState === VISIBILITY_STATE.MIXED
                  ? t("owner.visibility.mixedWarning", {
                      defaultValue: "Choose whether to publish the remaining private items or make every public item private.",
                    })
                  : activeVisibilityState === VISIBILITY_STATE.PUBLIC
                    ? t("owner.visibility.unpublishWarning", {
                        defaultValue: "Existing public links will stop showing these items.",
                      })
                    : t("owner.publish.warning", {
                        defaultValue: "Anyone with your public link will be able to view the published items.",
                      })}
              </p>
              {visibilityError ? <p className="owner-publish-modal__error" role="alert">{visibilityError}</p> : null}
            </div>
            <div className="owner-publish-modal__actions">
              <button
                type="button"
                className="owner-publish-modal__button"
                onClick={() => setVisibilityModal(null)}
                disabled={visibilityLoading}
              >
                {t("common.cancel", { defaultValue: "Cancel" })}
              </button>
              {[VISIBILITY_STATE.PUBLIC, VISIBILITY_STATE.MIXED].includes(activeVisibilityState) ? (
                <button
                  type="button"
                  className="owner-publish-modal__button"
                  onClick={() => handleVisibilityChange(false)}
                  disabled={visibilityLoading || !activeVisibilitySummary?.publicCount}
                >
                  {visibilityLoading
                    ? t("common.saving", { defaultValue: "Saving..." })
                    : t("owner.visibility.unpublishConfirm", {
                        defaultValue: "Make {{count}} items private",
                        count: activeVisibilitySummary?.publicCount || 0,
                      })}
                </button>
              ) : null}
              {[VISIBILITY_STATE.PRIVATE, VISIBILITY_STATE.MIXED].includes(activeVisibilityState) ? (
                <button
                  type="button"
                  className="owner-publish-modal__button"
                  onClick={() => handleVisibilityChange(true)}
                  disabled={visibilityLoading || !activeVisibilitySummary?.privateCount}
                >
                  {visibilityLoading
                    ? t("common.saving", { defaultValue: "Saving..." })
                    : t("owner.publish.confirm", {
                        defaultValue: "Publish {{count}} items",
                        count: activeVisibilitySummary?.privateCount || 0,
                      })}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

